import { useEffect, useState, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiService } from "../backend/api";
import {
  ShieldCheck,
  Key,
  QrCode,
  X,
  Loader2,
  AlertCircle
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { LoginContext } from "../LoginContext";
import { getBaseUrl } from "../config/apiConfig";

const Project2FAVerify = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState(null);
  const [username, setUsername] = useState(null);
  const [has2FA, setHas2FA] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { loginInfo } = useContext(LoginContext);

  const fetch2FASetup = useCallback(async (user) => {
    try {
      setIsLoading(true);
      setError("");
      const data = await apiService.request(`${getBaseUrl()}/api/user/2fa/setup`, {
        method: "POST",
        body: JSON.stringify({ username: user })
      });

      setHas2FA(data.has2FA);

      if (data.qrCodeImageUrl) {
        setQrCodeImageUrl(data.qrCodeImageUrl);
      } else {
        setQrCodeImageUrl(null);
      }
    } catch (error) {
      console.error("Error fetching 2FA setup:", error);
      setError("2FA-Einrichtung konnte nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = loginInfo?.username;
    if (!user) {
      navigate("/projects");
      return;
    }
    setUsername(user);
    fetch2FASetup(user);
  }, [loginInfo, navigate, fetch2FASetup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length < 6) return;

    setError("");
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/2fa/verify`, {
        method: "POST",
        body: JSON.stringify({ username, token: code, forceNew: true })
      });

      if (data.verified) {
        localStorage.setItem("last2FAVerified", Date.now().toString());
        navigate(location.state?.returnTo || "/projects");
      } else {
        setError(data.message || t("project2faVerify.invalidCode"));
      }
    } catch (err) {
      setError(err.response?.data?.message || t("project2faVerify.verificationFailed"));
      console.error("2FA verification error:", err);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[9999] flex items-center justify-center p-4 font-sans">
      <div className={`bg-white dark:bg-slate-800 w-full ${qrCodeImageUrl ? 'max-w-[640px]' : 'max-w-[420px]'} rounded-[2rem] shadow-2xl overflow-hidden border border-white/20 dark:border-slate-700 animate-in zoom-in duration-300`}>

        {/* Header Section */}
        <div className="bg-[#0f7699] dark:bg-[#4fc3f7]/20 p-6 text-center relative border-b border-white/10 dark:border-slate-700">
          <div className="absolute top-0 right-0 p-4">
             <button onClick={() => navigate("/")} className="text-white/60 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-xl">
               <X size={20} />
             </button>
          </div>
          <div className="flex items-center justify-center space-x-4">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white backdrop-blur-sm border border-white/10 shadow-lg">
              <ShieldCheck size={24} strokeWidth={1.5} />
            </div>
            <h1 className="text-lg font-bold text-white leading-tight">
               {has2FA ? "Zwei-Faktor-Verifizierung" : "Sicherheit einrichten"}
            </h1>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8">
          {isLoading ? (
            <div className="py-8 flex flex-col items-center justify-center space-y-4">
              <Loader2 size={40} className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin" />
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Verbindung wird aufgebaut...</p>
            </div>
          ) : (
            <div className={`flex flex-col ${qrCodeImageUrl ? 'md:flex-row gap-10' : 'space-y-6'}`}>

              {/* Left Column: QR Code */}
              {qrCodeImageUrl && (
                <div className="flex flex-col items-center space-y-4 md:w-[220px] flex-shrink-0">
                  <div className="p-4 bg-white rounded-[1.5rem] border-2 border-slate-200 dark:border-slate-500 shadow-inner">
                    <img
                      src={qrCodeImageUrl}
                      alt="2FA QR Code"
                      className="w-36 h-36"
                    />
                  </div>
                  <div className="bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699]/10 dark:border-[#4fc3f7]/20 p-3 rounded-xl border w-full">
                    <div className="flex items-center space-x-2 mb-1.5 text-[#0f7699] dark:text-[#4fc3f7]">
                      <QrCode size={16} />
                      <span className="text-[10px] font-black uppercase tracking-wider">Scan Me</span>
                    </div>
                    <p className="text-[10px] leading-relaxed text-slate-500 dark:text-slate-400 font-medium italic">
                      Scannen Sie diesen Code mit Ihrer Authentifizierungs-App.
                    </p>
                  </div>
                </div>
              )}

              {/* Right Column: Form & Info */}
              <div className="flex-1 flex flex-col justify-between space-y-6">
                <div className="space-y-6">
                  {!has2FA && (
                    <div className="space-y-2">
                       <h2 className="text-xs font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7]">
                         Anleitung
                       </h2>
                       <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                         Um 2FA zu aktivieren, scannen Sie entweder den QR-Code links oder geben Sie den generierten Code unten ein, um Ihr Konto zu verifizieren.
                       </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest px-1 text-[#0f7699] dark:text-[#4fc3f7] opacity-70">
                         Einmal-Code
                       </label>
                       <div className="relative group">
                         <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-slate-500 group-focus-within:text-[#0f7699] dark:group-focus-within:text-[#4fc3f7] transition-colors" size={18} />
                         <input
                           type="text"
                           value={code}
                           onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           maxLength={6}
                           placeholder="000 000"
                           className="w-full h-12 pl-12 pr-6 bg-slate-50 dark:bg-slate-700 border-2 border-slate-100 dark:border-slate-600 rounded-xl focus:border-[#0f7699]/40 dark:focus:border-[#4fc3f7]/40 focus:bg-white dark:focus:bg-slate-600 transition-all text-xl font-bold tracking-[0.5em] text-slate-800 dark:text-white placeholder:text-slate-200 dark:placeholder:text-slate-500 outline-none caret-[#0f7699] dark:caret-[#4fc3f7]"
                         />
                       </div>
                    </div>

                    <button
                      type="submit"
                      disabled={code.length < 6}
                      className={`w-full h-12 rounded-xl font-bold shadow-lg shadow-blue-900/5 transition-all flex items-center justify-center space-x-3 active:scale-[0.98] ${code.length < 6 ? 'bg-slate-100 dark:bg-slate-700 text-slate-300 dark:text-slate-500' : 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900'}`}
                    >
                      <ShieldCheck size={18} />
                      <span className="text-sm font-bold">Bestätigen & Aktivieren</span>
                    </button>
                  </form>

                  {/* Status Messages */}
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-100 text-red-600 rounded-xl flex items-center space-x-3 animate-in fade-in slide-in-from-top-2">
                      <AlertCircle size={16} className="shrink-0" />
                      <p className="text-[11px] font-bold leading-tight">{error}</p>
                    </div>
                  )}

                </div>

                {/* Footer Actions */}
                <div className="pt-6 border-t border-slate-100 dark:border-slate-700 mt-auto">
                  <button
                     onClick={() => navigate("/")}
                     className="flex items-center justify-center h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all text-[10px] font-black uppercase tracking-widest w-full"
                  >
                     <span>Abbrechen</span>
                  </button>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Project2FAVerify;
